import Drawer from '@mui/material/Drawer'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import Divider from '@mui/material/Divider'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import CloseIcon from '@mui/icons-material/Close'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useCart } from '../cart/CartContext'
import { formatMoney } from '../../shared/money'
import OptionPicker from './OptionPicker'
import QuantityStepper from './QuantityStepper'
import type { CartItem } from '../../shared/types'

export default function CartDrawer({
  open,
  onClose,
  currency,
  showPrices,
}: {
  open: boolean
  onClose: () => void
  currency: string
  showPrices: boolean
}) {
  const { t } = useTranslation()
  const { items, subtotal, add, setQuantity, remove } = useCart()
  const navigate = useNavigate()
  // For an optioned line, "+" reopens the picker (prefilled) so the customer re-confirms the
  // variant; confirming the same choice adds one more, a different choice starts a new line.
  const [picking, setPicking] = useState<CartItem | null>(null)

  return (
    <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { width: { xs: '100%', sm: 400 }, bgcolor: '#FBF5EC' } }}>
      <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>{t('cart.title')}</Typography>
          <IconButton onClick={onClose} aria-label={t('cart.close')}>
            <CloseIcon />
          </IconButton>
        </Stack>
        <Divider />

        {items.length === 0 ? (
          <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
            {t('cart.empty')}
          </Typography>
        ) : (
          <Stack spacing={2} sx={{ flexGrow: 1, overflowY: 'auto', py: 2 }}>
            {items.map((item) => (
              <Stack key={item.key} direction="row" spacing={1} alignItems="center">
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <Typography variant="subtitle2" noWrap>
                    {item.product.name}
                  </Typography>
                  {item.selections && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {Object.entries(item.selections).map(([g, o]) => `${g}: ${o}`).join('; ')}
                    </Typography>
                  )}
                  <Typography variant="body2" color="text.secondary">
                    {/* Priceless shop (D-069): quantity only, no money. */}
                    {showPrices && item.product.price != null
                      ? `${formatMoney(item.product.price, currency)} × ${item.quantity} = ${formatMoney(item.product.price * item.quantity, currency)}`
                      : `× ${item.quantity}`}
                  </Typography>
                </Box>
                <QuantityStepper
                  value={item.quantity}
                  onDecrement={() => setQuantity(item.key, item.quantity - 1)}
                  onIncrement={() => (item.selections ? setPicking(item) : setQuantity(item.key, item.quantity + 1))}
                />
                <IconButton size="small" onClick={() => remove(item.key)} aria-label={t('common.remove')} sx={{ color: '#A0522D' }}>
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Stack>
            ))}
          </Stack>
        )}

        <Divider />
        {/* Priceless shop (D-069): no subtotal line. */}
        {showPrices && (
          <Stack direction="row" justifyContent="space-between" sx={{ py: 2 }}>
            <Typography variant="subtitle1">{t('common.subtotal')}</Typography>
            <Typography variant="subtitle1" fontWeight={700}>
              {formatMoney(subtotal, currency)}
            </Typography>
          </Stack>
        )}
        <Button
          variant="contained"
          size="large"
          fullWidth
          disabled={items.length === 0}
          sx={{ mt: showPrices ? 0 : 2, borderRadius: '14px', py: 1.5 }}
          onClick={() => {
            onClose()
            navigate('/checkout')
          }}
        >
          {t('cart.proceedToCheckout')}
        </Button>
      </Box>

      {picking && (
        <OptionPicker
          product={picking.product}
          initial={picking.selections}
          onClose={() => setPicking(null)}
          onAdd={(selections) => {
            add(picking.product, 1, selections)
            setPicking(null)
          }}
        />
      )}
    </Drawer>
  )
}
